import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { notify } from '../lib/notifications.js';
import { deviceTimeZone, isValidTimeZone } from '../lib/timezone.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';

const TARGETS = {
  brand: 'settings.timezoneTargetBrand',
  publishing: 'settings.timezoneTargetPublishing',
  autopilot: 'settings.timezoneTargetAutopilot',
};

async function readBrand(scope) {
  try {
    const response = await api.profile.getBrandProfile({ fresh: true });
    const profile = response?.data?.brandProfile || response?.brandProfile || response?.data || response;
    if (!profile?.id) throw new Error('Brand profile unavailable');
    if (scope.brandProfileId && profile.id !== scope.brandProfileId) throw new Error('Active brand changed');
    return profile;
  } catch (error) {
    if (error?.status === 404 && !scope.brandProfileId) return null;
    throw error;
  }
}

async function readPublishing() {
  try {
    const response = await api.profile.getPublishingPreferences({ fresh: true });
    const preferences = response?.publishingPreferences;
    if (!preferences || typeof preferences !== 'object' || Array.isArray(preferences)) {
      throw new Error('Publishing preferences unavailable');
    }
    return preferences;
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

async function readAutopilot(scope) {
  const response = await api.autopilot.getState({ brandProfileId: scope.brandProfileId, fresh: true });
  const state = response?.state;
  // Only explicit state:null proves absence. Errors/malformed responses must
  // never permit skipping an existing row and reporting a complete save.
  if (state !== null && (!state || typeof state !== 'object' || Array.isArray(state))) {
    throw new Error('Autopilot state unavailable');
  }
  return state;
}

const READERS = { brand: readBrand, publishing: readPublishing, autopilot: readAutopilot };
const loaded = data => ({ status: data === null ? 'missing' : 'ready', data });
const emptyResources = () => Object.fromEntries(Object.keys(TARGETS).map(key => [key, { status: 'loading', data: null }]));

/** Shared, explicit-save timezone state for Settings and dashboard quick access. */
export function useTimezoneSettings() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const brandContext = useBrand();
  const apiBrandId = api.getActiveBrandId();
  const scope = { brandProfileId: apiBrandId || brandContext?.activeBrandId || null };
  const scopeKey = JSON.stringify([user?.id, user?.organizationId, brandContext?.activeBrandId, apiBrandId, !!brandContext?.loading]);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const generation = useRef(null);
  const [resourceScope, setResourceScope] = useState(null);
  const [resources, setResources] = useState(emptyResources);
  const [value, setValue] = useState(deviceTimeZone);
  const [savedValue, setSavedValue] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveLock = useRef(null);

  const record = (key, resource) => setResources(previous => ({ ...previous, [key]: resource }));
  useEffect(() => {
    const token = {};
    generation.current = token;
    setResourceScope(scopeKey);
    setResources(emptyResources());
    setInitialized(false);
    setSavedValue(null);
    setValue(deviceTimeZone());
    setSaving(false);
    saveLock.current = null;
    const current = () => generation.current === token && scopeRef.current === scopeKey && api.getActiveBrandId() === apiBrandId;
    if (!user?.id || brandContext?.loading) return () => { generation.current = null; };
    for (const [key, read] of Object.entries(READERS)) {
      read(scope)
        .then(data => { if (current()) record(key, loaded(data)); })
        .catch(() => { if (current()) record(key, { status: 'error', data: null }); });
    }
    return () => { generation.current = null; };
  }, [scopeKey]);

  const loading = resourceScope !== scopeKey || Object.values(resources).some(resource => resource.status === 'loading');
  useEffect(() => {
    if (initialized || loading) return;
    // Preserve all valid stored choices, including UTC. Device time is only
    // a suggestion when no existing store has a valid timezone.
    const initial = [resources.brand.data?.timezone, resources.publishing.data?.timezone, resources.autopilot.data?.timezone]
      .find(isValidTimeZone) || null;
    setValue(initial || deviceTimeZone());
    setSavedValue(initial);
    setInitialized(true);
  }, [initialized, loading, resources]);

  const readErrors = Object.entries(resources)
    .filter(([, resource]) => resource.status === 'error')
    .map(([key]) => t(TARGETS[key]));
  const needsSync = value !== savedValue
    || readErrors.length > 0
    || resources.publishing.data?.timezone !== value
    || ['brand', 'autopilot'].some(key => resources[key].status === 'ready' && resources[key].data.timezone !== value);
  const canSave = initialized && !loading && !saving && needsSync;

  async function save() {
    const token = generation.current;
    const currentScope = () => token && generation.current === token && scopeRef.current === scopeKey && api.getActiveBrandId() === apiBrandId;
    if (saveLock.current || !canSave || !currentScope()) return false;
    saveLock.current = token;
    setSaving(true);
    const target = value;
    const current = { ...resources };
    const failures = [];
    try {
      // Resolve failed reads before any writes. Confirmed missing brand and
      // autopilot rows remain untouched; only missing publishing preferences
      // may be created by this explicit save.
      for (const key of Object.keys(TARGETS)) {
        if (current[key].status !== 'error') continue;
        try {
          current[key] = loaded(await READERS[key](scope));
          if (!currentScope()) return false;
          record(key, current[key]);
        } catch {
          failures.push(t(TARGETS[key]));
        }
      }
      if (!currentScope()) return false;
      const brandProfileId = current.brand.data?.id || scope.brandProfileId;
      if (current.autopilot.data?.brandProfileId && current.autopilot.data.brandProfileId !== brandProfileId) {
        failures.push(t(TARGETS.autopilot));
        record('autopilot', { status: 'error', data: null });
      }
      if (failures.length) {
        notify({ level: 'error', title: t('settings.timezoneSaveFailed', { target: failures.join(', ') }) });
        return false;
      }

      if (current.brand.data) {
        try {
          const next = { ...current.brand.data, timezone: target };
          await api.profile.updateBrandProfile({ brandProfileId, timezone: target });
          if (!currentScope()) return false;
          record('brand', loaded(next));
        } catch { failures.push(t(TARGETS.brand)); }
      }
      if (!currentScope()) return false;
      try {
        // The endpoint merges omissions; never replay a stale cadence snapshot.
        const next = { ...current.publishing.data, timezone: target };
        const response = await api.profile.updatePublishingPreferences({ timezone: target });
        if (!currentScope()) return false;
        record('publishing', loaded(response?.publishingPreferences || next));
      } catch { failures.push(t(TARGETS.publishing)); }
      if (!currentScope()) return false;
      if (current.autopilot.data) {
        try {
          // Omitted enabled/cadence are preserved by the autopilot endpoint.
          const response = await api.autopilot.setState({ timezone: target, brandProfileId });
          if (!currentScope()) return false;
          record('autopilot', loaded(response?.state || { ...current.autopilot.data, timezone: target }));
        } catch { failures.push(t(TARGETS.autopilot)); }
      }
      if (!currentScope()) return false;
      if (failures.length) {
        notify({ level: 'error', title: t('settings.timezoneSaveFailed', { target: failures.join(', ') }) });
        return false;
      }
      setSavedValue(target);
      notify({ level: 'success', title: t('settings.timezoneSaved') });
      return true;
    } finally {
      if (saveLock.current === token) {
        saveLock.current = null;
        setSaving(false);
      }
    }
  }

  return {
    brand: loading ? null : resources.brand.data,
    brandStatus: loading ? 'loading' : resources.brand.status,
    value, setValue, savedValue, initialized: initialized && !loading, loading, saving, readErrors, canSave, save,
    reset: () => setValue(savedValue || deviceTimeZone()),
    useDevice: () => setValue(deviceTimeZone()),
  };
}
